import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import Stepper from "../../components/stepper/Stepper";
import Modal, { type ModalAction } from "../../components/modal/Modal";
import ProjectCodeNameInput from "./components/ProjectCodeNameInput";
import ProjectObject from "./components/ProjectObject";
import ProjectEndClientInput from "./components/ProjectEndClientInput";
import ProjectCreationConfirm from "./components/ProjectCreationConfirm";
import { useCreateProjectMutation } from "../../app/services/projects";

const STEP_LABELS = ["コードネーム", "目的物", "エンドクライアント", "確認"];
const MIN_STEP = 1;
const MAX_STEP = STEP_LABELS.length;

/**
 * プロジェクト作成ウィザード（/project-creation）。
 *
 * Careers と同じ構成：fullscreen Modal のヘッダー左端の chevron_left で
 * `/projects` へ戻り、その右の Stepper でステップ進行を表示する。
 *
 * - Step1: コードネーム
 * - Step2: 目的物（旧 BE 由来のジャンル / プラットフォームタグから組み立て）
 * - Step3: エンドクライアント（任意）
 * - Step4: 入力内容の確認
 *
 * Step4 で POST /api/me/projects を呼ぶ。BE 側で作成者は自動的に
 * メンバーシップ position=PRODUCER として参加する。新 SPA からの作成は
 * 常に新決済モデルで運用する（`is_new_payment_model: true` を送る）。
 *
 * 成功後はダイアログモーダルを表示し「画面右下の INFO ボタンから残りの
 * 詳細情報を設定できる」ことを案内する。モーダルを閉じると作成された
 * プロジェクトの config タブ（`/:projectId?tab=config`）へ遷移し、
 * そのまま追加項目を編集できるようにする。
 */
const ProjectCreation: React.FC = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(MIN_STEP);
  const [codeName, setCodeName] = useState("");
  const [finalWork, setFinalWork] = useState("");
  const [endClient, setEndClient] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [createdProjectId, setCreatedProjectId] = useState<string | null>(null);
  const [createProject, { isLoading: isCreating }] = useCreateProjectMutation();

  const trimmedCodeName = codeName.trim();
  const trimmedFinalWork = finalWork.trim();
  const trimmedEndClient = endClient.trim();

  // Step2 に進んだ初回、目的物欄が空ならコードネームから雛形を仮入れする
  // （`「(コードネーム)（仮）」`）。旧 BE の artist_create_product でも同じ挙動。
  // 依存配列に finalWork を含めないのは意図的: 一度ユーザーが空文字に戻した
  // 後に再注入してしまうのを防ぐため、Step2 への遷移とコードネーム変更のみで
  // 走らせる。
  useEffect(() => {
    if (currentStep === 2 && finalWork === "" && trimmedCodeName !== "") {
      setFinalWork(`「${trimmedCodeName}（仮）」`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, trimmedCodeName]);

  const isStepValid = (step: number): boolean => {
    switch (step) {
      case 1:
        return trimmedCodeName.length > 0;
      case 2:
        return trimmedFinalWork.length > 0;
      case 3:
        // エンドクライアントは任意項目のため常に有効。
        return true;
      case 4:
        // 最終 POST 直前のガード。Step1/2 で必須にしているが、念のため
        // 両方の必須項目を再チェックする。
        return trimmedCodeName.length > 0 && trimmedFinalWork.length > 0;
      default:
        return false;
    }
  };
  const isCurrentStepValid = isStepValid(currentStep);

  // Stepper クリックによる移動は「確定済みのステップへ戻る」用途のみ許可する
  // （前進は「次へ」経由）。
  const canJumpToStep = (target: number): boolean => target < currentStep;

  const goToStep = (target: number) => {
    if (target < MIN_STEP || target > MAX_STEP) return;
    setCurrentStep(target);
  };

  const handleBack = () => {
    if (currentStep <= MIN_STEP) return;
    setCurrentStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    setSubmitError(null);
    try {
      const response = await createProject({
        final_work: trimmedFinalWork,
        code_name: trimmedCodeName,
        end_client: trimmedEndClient,
        is_new_payment_model: true,
      }).unwrap();
      const projectId = response?.data?.project?.project_id;
      if (projectId) {
        setCreatedProjectId(projectId);
      } else {
        // 想定外: 201 を受けつつ project_id が無い場合は一覧に戻して再確認させる。
        navigate("/projects");
      }
    } catch (err) {
      console.error("[ProjectCreation] プロジェクト作成に失敗:", err);
      setSubmitError("プロジェクトの作成に失敗しました。時間をおいて再度お試しください。");
    }
  };

  const handleNext = () => {
    if (!isCurrentStepValid) return;
    if (currentStep < MAX_STEP) {
      setCurrentStep((prev) => prev + 1);
      return;
    }
    handleSubmit();
  };

  // 作成直後はそのまま config（設定）タブへ。残りの詳細項目をすぐ
  // 編集できるよう、ProjectWrapper の `?tab=config` ディープリンクを使う。
  const handleCloseSuccessModal = () => {
    if (createdProjectId) {
      navigate(`/${createdProjectId}?tab=config`);
    } else {
      navigate("/projects");
    }
  };

  const isLastStep = currentStep === MAX_STEP;
  const isFirstStep = currentStep === MIN_STEP;

  const primaryAction: ModalAction = {
    label: isLastStep ? "作成する" : "次へ",
    onClick: handleNext,
    disabled: !isCurrentStepValid || isCreating,
    loading: isLastStep && isCreating,
    rightIcon: isLastStep ? undefined : "chevron_right",
  };

  // Step1 では「戻る」を出さない（chevron_left で /projects へ戻る導線が
  // ヘッダー側にあるため、フッターに重複させない）。Step2 以降は前ステップへ
  // 戻る secondaryAction を表示する。
  const secondaryAction: ModalAction | undefined = isFirstStep
    ? undefined
    : {
        label: "戻る",
        onClick: handleBack,
        disabled: isCreating,
        leftIcon: "chevron_left",
      };

  return (
    <>
      <Modal
        variant="fullscreen"
        onBack={() => navigate("/projects")}
        headerExtras={
          <Stepper
            stepLabels={STEP_LABELS}
            current={currentStep}
            onSelectStep={goToStep}
            isStepClickable={canJumpToStep}
            showCheckOnComplete
          />
        }
        bodyWidth="reading"
        primaryAction={primaryAction}
        secondaryAction={secondaryAction}
      >
        <>
          {currentStep === 1 && (
            <ProjectCodeNameInput value={codeName} onChange={setCodeName} />
          )}
          {currentStep === 2 && (
            <ProjectObject value={finalWork} onChange={setFinalWork} />
          )}
          {currentStep === 3 && (
            <ProjectEndClientInput value={endClient} onChange={setEndClient} />
          )}
          {currentStep === 4 && (
            <ProjectCreationConfirm
              codeName={trimmedCodeName}
              finalWork={trimmedFinalWork}
              endClient={trimmedEndClient}
            />
          )}

          {submitError && (
            <p className="bodytext-13 u-text-green u-pl4 u-mt16">{submitError}</p>
          )}
        </>
      </Modal>

      {createdProjectId && (
        <Modal
          variant="dialog"
          title="プロジェクトを作成しました"
          description={
            "ようこそ、プロデューサーとして新しいプロジェクトへ。\n" +
            "概要やスケジュール、タグなどその他の詳細情報は、画面右下の INFO ボタンからいつでも設定できます。"
          }
          primaryAction={{
            label: "プロジェクトを開く",
            onClick: handleCloseSuccessModal,
            // 既存のステップナビゲーション「次へ」と同じ chevron_right を採用し、
            // 「進む」系の右アイコンスタイルを SPA 全体で揃える。
            rightIcon: "chevron_right",
          }}
        />
      )}
    </>
  );
};

export default ProjectCreation;
